import { ApiError, HttpStatusCode } from "@zuplo/errors";
import { Hono } from "hono";
import { bodyLimit } from "hono/body-limit";
import { cors } from "hono/cors";
import { getLocalEditorConfig, type LocalEditorLabels } from "./config.js";
import { editorConfig } from "./constants.js";
import { createErrorHandler, createNotFoundHandler } from "./error-handler.js";
import type { CommonLogger, SseClient } from "./interfaces.js";
import type {
  ChangeNotifier,
  EditorPreferencesPort,
  FileSystemPort,
} from "./ports.js";
import { createApiRoutes } from "./routes/api.js";

/**
 * The built SPA, however the platform happens to hold it.
 *
 * `serve` is handed the request rather than the Hono context this app is holding
 * when it calls: hono is bundled into the published package, so nothing that
 * installs it can resolve a type naming `Context`. Which costs nothing here,
 * because a request is already what the Worker's implementation wants —
 * Cloudflare's assets binding takes one — and the Node implementation runs
 * `serveStatic` in a Hono app of its own to get a real context rather than a
 * synthesised one.
 */
export interface AssetSource {
  /**
   * The `index.html` template, or `undefined` when there's no client build.
   *
   * Returned as a string because the config injection rewrites it per request.
   */
  readDocument(): Promise<string | undefined>;

  /** The built asset the request is for, or `undefined` if there isn't one. */
  serve(request: Request): Promise<Response | undefined>;
}

/**
 * A request handler, in the shape every listener the editor runs behind already
 * wants: `serve` from `@hono/node-server` takes exactly this, and so does a
 * Worker's `fetch` export.
 *
 * Stated structurally instead of as the `Hono` the app really is, for the same
 * reason `AssetSource.serve` takes a request — hono is bundled, so it can't
 * appear in the published types.
 *
 * The trailing arguments are the Worker's `env` and `ExecutionContext`, which
 * Hono passes through to route handlers; nothing in this package reads them.
 * They're `any[]` rather than `unknown[]` because parameters are checked
 * contravariantly: the type has to accept Hono's own `fetch`, whose third
 * parameter is a Cloudflare `ExecutionContext` and so cannot be handed an
 * `unknown`. Here `any` is how you say "this type doesn't constrain these",
 * which is the opposite of what `unknown` says in this position.
 */
export type EditorFetch = (
  request: Request,
  // biome-ignore lint/suspicious/noExplicitAny: see above.
  ...rest: any[]
) => Response | Promise<Response>;

export interface EditorAppOptions {
  /** The project the editor reads and writes. */
  fs: FileSystemPort;

  /** Where the user's external-editor choice is kept. */
  preferences: EditorPreferencesPort;

  /**
   * The built SPA. Omit to run without one — Vite serves the client in
   * development, and this app only answers `/api` in that case.
   */
  assets?: AssetSource;

  /**
   * Reports files changing underneath the editor, so the SPA can reload them.
   * Omit to leave live-reload dormant.
   */
  changes?: ChangeNotifier;

  logger: CommonLogger;

  /** The gateway the test console relays requests to. */
  deploymentUrl: string;

  /**
   * The project's location on the machine running the editor. Used only to build
   * external-editor deep links; all file access goes through `fs`.
   */
  sourceDirectory: string;

  /** Overrides for the account/environment names the portal displays. */
  labels?: LocalEditorLabels;
}

export interface EditorApp {
  /**
   * Hand this to whatever is listening — `@hono/node-server` on Node, the
   * Worker's default export on Cloudflare.
   */
  fetch: EditorFetch;

  /** Releases SSE clients and stops listening for changes. */
  dispose: () => Promise<void>;
}

const staticFileExtensions = [".css", ".js", ".json", ".svg", ".png", ".ico"];

/**
 * Builds the editor's HTTP app from injected platform capabilities.
 *
 * This is the whole server minus the platform: no listener, no filesystem, no
 * file watcher, no logger implementation. `LocalEditorServer` wraps it for Node
 * and the preview Worker wraps it for Cloudflare, so both run these exact
 * routes rather than approximations of each other.
 */
export function createEditorApp(options: EditorAppOptions): EditorApp {
  const {
    fs,
    preferences,
    assets,
    changes,
    logger,
    deploymentUrl,
    sourceDirectory,
    labels,
  } = options;

  const app = new Hono();
  const sseClients = new Set<SseClient>();

  app.onError(createErrorHandler(logger));
  app.notFound(createNotFoundHandler());

  // Middleware only wraps routes registered after it, so this has to come
  // before the routes below.
  app.use(
    bodyLimit({
      maxSize: editorConfig.server.bodyLimit,
      onError: () => {
        throw new ApiError({
          type: "https://zup.fail/http-status/413",
          title: "Payload Too Large",
          status: HttpStatusCode.PAYLOAD_TOO_LARGE,
          detail: `Request body exceeds the maximum size of ${editorConfig.server.bodyLimit} bytes`,
        });
      },
    }),
  );
  app.use(
    cors({
      origin: (origin) => origin ?? "*",
      allowMethods: [
        "GET",
        "POST",
        "PATCH",
        "PUT",
        "DELETE",
        "OPTIONS",
        "HEAD",
      ],
      allowHeaders: ["*"],
      exposeHeaders: ["*"],
    }),
  );

  app.route(
    "/api",
    createApiRoutes({
      fs,
      editorPreferences: preferences,
      sourceDirectory,
      logger,
      sseClients,
      deploymentUrl,
    }),
  );

  if (assets) {
    app.on(["GET", "HEAD"], "*", async (c) => {
      const isDocumentRequest =
        c.req.header("accept")?.includes("text/html") &&
        !staticFileExtensions.some((ext) => c.req.path.endsWith(ext));

      if (!isDocumentRequest) {
        return (
          (await assets.serve(c.req.raw)) ??
          // The asset source hands misses back rather than 404ing itself, so
          // they get the same problem+json every other miss does.
          c.notFound()
        );
      }

      // Every navigation gets index.html — the SPA owns routing.
      const template = await assets.readDocument();
      if (!template) {
        // No client build yet, which is normal in development: Vite is in front
        // and this handler is never reached there.
        return c.notFound();
      }

      const localEditorConfig = await getLocalEditorConfig({
        deploymentUrl,
        fs,
        logger,
        labels,
      });

      // Inject localEditorConfig into the HTML
      const html = template.replace(
        `"{{localEditorConfig}}"`, // Note the quotes are correct
        // A replacer function, so `$&` and friends in the JSON aren't treated
        // as substitution patterns.
        () => JSON.stringify(localEditorConfig),
      );

      return c.html(html);
    });
  }

  const stopWatching = changes?.onChange((filePath) => {
    // Broadcast to all SSE clients
    const data = JSON.stringify({ type: "change", filePath });
    for (const client of sseClients) {
      client.send("change", data).catch((err) => {
        logger.warn({ err }, "Error sending SSE change event");
      });
    }

    logger.info(`File changed: ${filePath}`);
  });

  const dispose = async (): Promise<void> => {
    stopWatching?.();

    for (const client of sseClients) {
      try {
        if (!client.closed) {
          await client.close();
        }
      } catch (err) {
        logger.warn({ err }, "Error closing SSE client");
      }
    }
    sseClients.clear();
  };

  return { fetch: app.fetch, dispose };
}
